<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import IconsRemoveBgFeature from '../../icons/RemoveBgFeature.vue';
import ImageLazy from '@/components/ImageLazy.vue';

// Background removal slider
const removeBgSliderPosition = ref(60);
const removeBgContainerRef = ref<HTMLElement | null>(null);
const isDraggingRemoveBg = ref(false);

const handleRemoveBgMouseDown = (event: MouseEvent | TouchEvent) => {
  event.preventDefault();
  isDraggingRemoveBg.value = true;
  updateRemoveBgSliderPosition(event);
};

const handleRemoveBgMouseMove = (event: MouseEvent | TouchEvent) => {
  if (!isDraggingRemoveBg.value) return;
  event.preventDefault();
  updateRemoveBgSliderPosition(event);
};

const handleRemoveBgMouseUp = () => {
  isDraggingRemoveBg.value = false;
};

const updateRemoveBgSliderPosition = (event: MouseEvent | TouchEvent) => {
  if (!removeBgContainerRef.value) return;
  const rect = removeBgContainerRef.value.getBoundingClientRect();
  let clientX: number;
  if ('touches' in event) {
    const touch = event.touches[0];
    if (!touch) return;
    clientX = touch.clientX;
  } else {
    clientX = event.clientX;
  }
  const x = clientX - rect.left;
  const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
  removeBgSliderPosition.value = percentage;
};

onMounted(() => {
  document.addEventListener('mousemove', handleRemoveBgMouseMove as EventListener);
  document.addEventListener('mouseup', handleRemoveBgMouseUp);
  document.addEventListener('touchmove', handleRemoveBgMouseMove as EventListener, {
    passive: false,
  });
  document.addEventListener('touchend', handleRemoveBgMouseUp);
});

onUnmounted(() => {
  document.removeEventListener('mousemove', handleRemoveBgMouseMove as EventListener);
  document.removeEventListener('mouseup', handleRemoveBgMouseUp);
  document.removeEventListener('touchmove', handleRemoveBgMouseMove as EventListener);
  document.removeEventListener('touchend', handleRemoveBgMouseUp);
});
</script>

<template>
  <div class="group relative rounded-3xl bg-white overflow-hidden">
    <!-- Image comparison slider - fills entire card -->
    <div
      ref="removeBgContainerRef"
      class="relative w-full aspect-[3/4] cursor-ew-resize select-none"
      @mousedown="handleRemoveBgMouseDown"
      @touchstart.prevent="handleRemoveBgMouseDown"
    >
      <!-- Content overlay -->
      <div
        class="absolute top-0 left-0 right-0 z-20 p-6 md:p-8 bg-gradient-to-b from-black/30 to-transparent pointer-events-none"
      >
        <IconsRemoveBgFeature class="w-5 h-5 text-white mb-4" />
        <p class="text-lg md:text-xl leading-[1.4]">
          <span class="text-white">{{ $t('landing_home.cards.remove_bg.primary') }}</span>
          <span class="text-white/60">{{ $t('landing_home.cards.remove_bg.secondary') }}</span>
        </p>
      </div>
      <!-- Layer 1: Background (xmas.png) -->
      <ImageLazy
        src="/images/removebg/cat.webp"
        alt="Christmas background"
        class="absolute inset-0 w-full h-full"
        image-class="w-full h-full object-cover object-center"
      />
      <!-- Layer 2: Original image with background (toberemoved.png) - clips from right -->
      <div
        class="absolute inset-0 overflow-hidden"
        :style="{ clipPath: `inset(0 ${100 - removeBgSliderPosition}% 0 0)` }"
      >
        <ImageLazy
          src="/images/removebg/toberemoved.webp"
          alt="Original image with background"
          class="w-full h-full"
          image-class="w-full h-full object-cover object-center"
        />
      </div>
      <!-- Layer 3: Removed background image (removed.png) - clips from left -->
      <div
        class="absolute inset-0 overflow-hidden"
        :style="{ clipPath: `inset(0 0 0 ${removeBgSliderPosition}%)` }"
      >
        <ImageLazy
          src="/images/removebg/removed.webp"
          alt="Image with background removed"
          class="w-full h-full"
          image-class="w-full h-full object-cover object-center"
        />
      </div>
      <!-- Slider line -->
      <div
        class="absolute top-0 bottom-0 w-[1px] bg-white shadow-[0_0_10px_rgba(0,0,0,0.3)] z-10 pointer-events-none"
        :style="{ left: `${removeBgSliderPosition}%`, transform: 'translateX(-50%)' }"
      >
        <!-- Slider handle -->
        <div
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 bg-white rounded-full shadow-lg flex items-center justify-center"
        ></div>
      </div>
    </div>
  </div>
</template>
